/**
 * Страница привязки email (без модалки, как в админке — отдельная страница).
 * Состояние (step, email, code) сохраняется в sessionStorage, чтобы пережить
 * переключение приложений (например, копирование кода из почты) без потери ввода.
 */

import { useState, useCallback, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Layout } from "../../components/layout/index.ts";
import { getTelegram } from "../../lib/telegram.ts";
import { useAuth } from "../../lib/auth.tsx";
import { useAddEmail, useVerifyEmail } from "@/features/profile/hooks";
import { safeJsonParse } from "../../lib/json-utils.ts";
import { createLogger } from "../../lib/logger.ts";
import { getErrorMessage } from "../../lib/graphql.ts";

const profileLog = createLogger("Profile");

const EMAIL_FLOW_STORAGE_KEY = "one-church:profile-email-flow";

type Step = "email" | "code";

interface EmailFlowState {
  step: Step;
  email: string;
  code: string;
}

function loadEmailFlowState(): Partial<EmailFlowState> | null {
  const raw = sessionStorage.getItem(EMAIL_FLOW_STORAGE_KEY);
  if (!raw) return null;
  
  const parsed = safeJsonParse<Partial<EmailFlowState> | null>(raw, null);
  if (!parsed || typeof parsed !== "object") return null;
  return parsed;
}

function saveEmailFlowState(state: EmailFlowState): void {
  try {
    sessionStorage.setItem(EMAIL_FLOW_STORAGE_KEY, JSON.stringify(state));
  } catch {
    // ignore
  }
}

function clearEmailFlowState(): void {
  try {
    sessionStorage.removeItem(EMAIL_FLOW_STORAGE_KEY);
  } catch {
    // ignore
  }
}

export function ProfileEmailPage() {
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const tg = getTelegram();
  const restored = loadEmailFlowState();
  const [step, setStep] = useState<Step>(restored?.step ?? "email");
  const [email, setEmail] = useState(restored?.email ?? "");
  const [code, setCode] = useState(restored?.code ?? "");
  const [error, setError] = useState<string | null>(null);
  const emailInputRef = useRef<HTMLInputElement>(null);
  const codeInputRef = useRef<HTMLInputElement>(null);

  // URQL hooks
  const { addEmail, isLoading: isAddingEmail } = useAddEmail();
  const { verifyEmail, isLoading: isVerifying } = useVerifyEmail();

  const isLoading = isAddingEmail || isVerifying;

  useEffect(() => {
    saveEmailFlowState({ step, email, code });
  }, [step, email, code]);

  useEffect(() => {
    if (!tg) return;
    tg.BackButton.show();
    const handleBack = () => navigate(-1);
    tg.BackButton.onClick(handleBack);
    return () => {
      tg.BackButton.offClick(handleBack);
      tg.BackButton.hide();
    };
  }, [tg, navigate]);

  useEffect(() => {
    if (step === "email") emailInputRef.current?.focus();
    else if (step === "code") codeInputRef.current?.focus();
  }, [step]);

  const handleEmailSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      setError(null);
      try {
        const result = await addEmail(email);
        if (result.result === "SENT") setStep("code");
        else if (result.result === "MERGE_REQUIRED" || result.result === "ALREADY_LINKED")
          setError("Этот email уже привязан к другому аккаунту. Введите другой email.");
      } catch (err) {
        setError(getErrorMessage(err, "Ошибка отправки кода"));
      }
    },
    [email, addEmail]
  );

  const handleCodeSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      setError(null);
      try {
        await verifyEmail(email, code);
        clearEmailFlowState();
        navigate("/profile/settings", { replace: true });
        refresh().catch((err) => {
          profileLog.debug("Non-fatal: refresh after email verify failed", err);
        });
      } catch (err) {
        setError(getErrorMessage(err, "Неверный код"));
      }
    },
    [email, code, refresh, navigate, verifyEmail]
  );

  const handleResend = useCallback(async () => {
    setError(null);
    try {
      await addEmail(email);
      setCode("");
    } catch (err) {
      setError(getErrorMessage(err, "Ошибка повторной отправки"));
    }
  }, [email, addEmail]);

  return (
    <Layout>
      <h1 className="page-title">
        {step === "email" ? "Добавить email" : "Подтвердите email"}
      </h1>
      <p className="text-[13px] text-slate-500 mb-4">
        {step === "email"
          ? "Введите email для входа в PWA"
          : `Код отправлен на ${email}`}
      </p>

      <div className="rounded-2xl bg-white/95 shadow-sm p-4">
        {step === "email" ? (
          <form onSubmit={handleEmailSubmit}>
            <label className="block mb-4">
              <span className="text-sm font-medium text-slate-700 mb-1.5 block">
                Email
              </span>
              <input
                ref={emailInputRef}
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="example@email.com"
                required
                disabled={isLoading}
                className="w-full px-4 py-3 rounded-xl border border-slate-200 bg-slate-50 text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-[var(--color-accent)] focus:border-transparent disabled:opacity-50"
              />
            </label>
            {error && <p className="text-sm text-red-600 mb-4">{error}</p>}
            <button
              type="submit"
              disabled={isLoading || !email.includes("@")}
              className="btn-primary w-full"
            >
              {isLoading ? "Отправка..." : "Отправить код"}
            </button>
          </form>
        ) : (
          <form onSubmit={handleCodeSubmit}>
            <label className="block mb-4">
              <span className="text-sm font-medium text-slate-700 mb-1.5 block">
                Код из письма
              </span>
              <input
                ref={codeInputRef}
                type="text"
                inputMode="numeric"
                value={
                  code.length <= 3
                    ? code
                    : `${code.slice(0, 3)}-${code.slice(3, 6)}`
                }
                onChange={(e) =>
                  setCode(e.target.value.replace(/\D/g, "").slice(0, 6))
                }
                placeholder="123-456"
                required
                maxLength={7}
                disabled={isLoading}
                className="w-full px-4 py-3 rounded-xl border border-slate-200 bg-slate-50 text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-[var(--color-accent)] focus:border-transparent disabled:opacity-50 text-center text-xl tracking-widest font-mono"
              />
            </label>

            {error && <p className="text-sm text-red-600 mb-4">{error}</p>}
            <button
              type="submit"
              disabled={isLoading || code.length < 6}
              className="btn-primary w-full mb-3"
            >
              {isLoading ? "Проверка..." : "Подтвердить"}
            </button>
            <div className="flex items-center justify-between text-sm">
              <button
                type="button"
                onClick={() => {
                  setStep("email");
                  setCode("");
                  setError(null);
                }}
                className="text-slate-500 hover:text-slate-700"
              >
                ← Изменить email
              </button>
              <button
                type="button"
                onClick={handleResend}
                disabled={isLoading}
                className="text-[var(--color-accent)] hover:underline disabled:opacity-50"
              >
                Отправить снова
              </button>
            </div>
          </form>
        )}
      </div>
    </Layout>
  );
}
